// @ts-check
/* eslint-disable no-multi-spaces, prefer-object-spread, sort-imports */
import FontAwesomeIcon from "react-native-vector-icons/FontAwesome.js"
import FontAwesome5Icon from "react-native-vector-icons/FontAwesome5.js"
import FontAwesome6Icon from "react-native-vector-icons/FontAwesome6.js"
import MaterialIconsIcon from "react-native-vector-icons/MaterialIcons.js"
import memo from "set-state-compare/build/memo.js"
import PropTypes from "prop-types"
import React, {useMemo} from "react"
import {StyleSheet} from "react-native"
import {ShapeComponent, shapeComponent} from "set-state-compare/build/shape-component.js"
import {useMergedStyle} from "./default-style"

const FontAwesomeGlyphMap = FontAwesomeIcon.getRawGlyphMap()
const FontAwesome5GlyphMap = FontAwesome5Icon.getRawGlyphMap()
const FontAwesome6GlyphMap = FontAwesome6Icon.getRawGlyphMap()
const MaterialIconsGlyphMap = MaterialIconsIcon.getRawGlyphMap()

const iconMap = {
  pencil: "FontAwesome",
  remove: "FontAwesome",
  search: "FontAwesome"
}

/**
 * @typedef {object} Props
 * @property {string|number} [color]
 * @property {object} [dataSet]
 * @property {string} name
 * @property {number} [size]
 * @property {import("react-native").StyleProp<import("react-native").TextStyle>} [style] Only `color` is forwarded.
 * @property {("FontAwesome"|"FontAwesome5"|"FontAwesome6"|"MaterialIcons")} [version]
 */
/** @typedef {Record<string, never>} State */
export default memo(shapeComponent(/** @augments {ShapeComponent<Props, State>} */ class ApiMakerUtilsIcon extends ShapeComponent {
  static propTypes = {
    color: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    dataSet: PropTypes.object,
    name: PropTypes.string.isRequired,
    size: PropTypes.number,
    style: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.number]),
    version: PropTypes.oneOf(["FontAwesome", "FontAwesome5", "FontAwesome6", "MaterialIcons"])
  }

  render() {
    const {dataSet, name, style, version, ...restProps} = this.props
    const {stylesList: inheritedStylesList} = useMergedStyle(null, "Text")
    let actualVersion = version

    if (!actualVersion) {
      if (name in iconMap) {
        actualVersion = iconMap[name]
      } else if (name in FontAwesome6GlyphMap) {
        actualVersion = "FontAwesome6"
      } else if (name in FontAwesome5GlyphMap) {
        actualVersion = "FontAwesome5"
      } else if (name in FontAwesomeGlyphMap) {
        actualVersion = "FontAwesome"
      } else if (name in MaterialIconsGlyphMap) {
        actualVersion =  "MaterialIcons"
      } else {
        actualVersion = "FontAwesome"
      }
    }

    // Only forward some styles like color from inherited Text styles.
    const actualStylesList = useMemo(() => {
      const actualStylesList = []

      for (const inheritedStyle of inheritedStylesList) {
        const flattenedStyle = StyleSheet.flatten(inheritedStyle) || {}

        if ("color" in flattenedStyle) {
          actualStylesList.push({color: flattenedStyle.color})
        }
      }

      if (Array.isArray(style)) {
        for (const propStyle of style) {
          if (propStyle) {
            actualStylesList.push(propStyle)
          }
        }
      } else if (style) {
        actualStylesList.push(style)
      }

      return actualStylesList
    }, [inheritedStylesList, style])

    const actualDataSet = useMemo(() => Object.assign({name, version: actualVersion}, dataSet), [actualVersion, dataSet, name])

    if (actualVersion == "FontAwesome") {
      return <FontAwesomeIcon dataSet={actualDataSet} name={name} style={actualStylesList} {...restProps} />
    } else if (actualVersion == "FontAwesome5") {
      return <FontAwesome5Icon dataSet={actualDataSet} name={name} style={actualStylesList} {...restProps} />
    } else if (actualVersion == "FontAwesome6") {
      return <FontAwesome6Icon dataSet={actualDataSet} name={name} style={actualStylesList} {...restProps} />
    } else if (actualVersion == "MaterialIcons") {
      return <MaterialIconsIcon dataSet={actualDataSet} name={name} style={actualStylesList} {...restProps} />
    } else {
      throw new Error(`Unknown version: ${actualVersion}`)
    }
  }
}))
